import { PlusOutlined } from "@ant-design/icons"
import { Button, Checkbox, Form, Spin } from "antd"
import { useWatch } from "antd/es/form/Form"
import dayjs from "dayjs"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { receiveErrors } from "modules/errors/reducer"
import { serialize } from "object-to-formdata"
import { useEffect, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { getGuideTrip, updateTrip } from "store/trips"
import { Card } from "ui/Card"
import { CustomForm } from "ui/Form"
import { DateRangeBlock } from "./DateRangeBlock"
import { DaysAndHours } from "./DaysAndHours"
import { LengthAndRates } from "./LengthAndRates"
import { SeasonTypeSelector } from "./SeasonTypeSelector"
import { PRICING_TYPES, SEASON_TYPES } from "./utils/constants"
import { setFormTouched } from "store/proNewListingForms"

export const ListingScheduling = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [form] = Form.useForm()
  const watchedStartDates = useWatch([], form)
  const trip = useSelector(modelSelector("trip"))
  const [isSeasonalExperience, setIsSeasonalExperience] = useState(false)
  const [timeBlocks, setTimeBlocks] = useState([0])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if ("seasonal" in trip) {
      setIsSeasonalExperience(trip.seasonal)
      if (trip.available_date_ranges?.length > 0) {
        setTimeBlocks(Array.from({ length: trip.available_date_ranges.length }, (_, i) => i))
      }
    } else {
      setIsSeasonalExperience(false)
      setTimeBlocks([])
    }
  }, [])

  const handleSeasonChange = (e) => {
    setIsSeasonalExperience(e.target.value === SEASON_TYPES.SEASONAL)
  }

  const handleAddTimeBlock = () => {
    setTimeBlocks((prevBlocks) => {
      const nextIndex = prevBlocks.length ? Math.max(...prevBlocks) + 1 : 0
      return [...prevBlocks, nextIndex]
    })
  }

  const handleRemoveTimeBlock = (index) => {
    setTimeBlocks((prevBlocks) => prevBlocks.filter((blockIndex) => blockIndex !== index))
  }

  const prepareUpdateData = async (validateForm = false) => {
    const tripPrices = trip.trip_prices || []
    if (tripPrices.length === 0) {
      dispatch(
        receiveErrors({
          data: { message: { listing_rates: [t("experiences.scheduling.sections.length_and_rates.no_rates_error_message")] } },
          showToast: false
        })
      )
      throw t("experiences.scheduling.sections.length_and_rates.no_rates_error_message")
    }
    const values = validateForm ? await form.validateFields() : form.getFieldsValue(true)

    const updateData = {
      time_zone: values.time_zone,
      available_days: values.available_days,
      available_hours: values.available_hours
    }

    if (!isSeasonalExperience && trip?.seasonal) {
      updateData.seasonal = false
    } else if (isSeasonalExperience) {
      updateData.seasonal = true
      updateData.recurring_yearly = values.recurringYearly

      const formatDateRanges = (values, timeBlocks, keyMap) =>
        timeBlocks
          .map((blockIndex) => {
            const startDate = values[`seasonStart_${blockIndex}`]
            const endDate = values[`seasonEnd_${blockIndex}`]

            if (startDate && endDate) {
              return {
                [keyMap.start]: startDate.format("YYYY-MM-DD"),
                [keyMap.end]: endDate.format("YYYY-MM-DD")
              }
            }
            return null
          })
          .filter(Boolean)

      const dateRanges = formatDateRanges(values, timeBlocks, {
        start: "start",
        end: "end"
      })

      const seasonalTripsAttributes = formatDateRanges(values, timeBlocks, {
        start: "start_date",
        end: "end_date"
      })

      if (dateRanges.length > 0) {
        updateData.available_date_ranges = dateRanges
        updateData.seasonal_trips_attributes = seasonalTripsAttributes
      }
    }

    updateData.pricing_type = values.pricing_type
    updateData.full_payment_required = values.full_payment_required
    updateData.deposit_percentage = values.deposit_percentage
    updateData.available_hours = values?.available_hours
      ? values?.available_hours.map(({ start, end }) => ({
          start: start.format("HH:mm"),
          end: end.format("HH:mm")
        }))
      : []
    updateData.trip_slots_attributes = values?.available_hours
      ? values?.available_hours.map(({ start, end }) => ({
          start_time: start.format("HH:mm"),
          end_time: end.format("HH:mm")
        }))
      : []
    return { ...values, ...updateData }
  }

  const onFinish = async (callback) => {
    try {
      setIsLoading(true)
      const updateData = await prepareUpdateData(true)
      const formData = serialize({ trip: updateData }, { allowEmptyArrays: true })
      await dispatch(updateTrip(id, formData))
        .then(() => {
          dispatch(setFormTouched("scheduling", true))
          callback?.()
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    } catch (error) {
      setIsLoading(false)
      console.error("Form validation failed:", error)
    }
  }

  const getAllSelectedDateRanges = useMemo(() => {
    return timeBlocks
      .map((blockIndex) => {
        const start = form.getFieldValue(`seasonStart_${blockIndex}`)
        const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
        if (start && end) {
          return { blockIndex, start, end }
        }
        return null
      })
      .filter(Boolean)
  }, [form, timeBlocks, watchedStartDates])

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <CustomForm
        form={form}
        layout="vertical"
        autoComplete="off"
        initialValues={{
          ...trip,
          pricing_type: trip.pricing_type || PRICING_TYPES.PER_PERSON,
          deposit_percentage: trip.deposit_percentage || 10
        }}
        style={{ maxWidth: "984px" }}
      >
        <Card className="mb-3xl" title={t("listings.tabs.scheduling.sections.seasons.title")}>
          <SeasonTypeSelector onChange={handleSeasonChange} value={isSeasonalExperience ? SEASON_TYPES.SEASONAL : SEASON_TYPES.ALL_YEAR} />
          {isSeasonalExperience && (
            <>
              {timeBlocks.map((blockIndex) => {
                const dateRange = trip?.available_date_ranges?.[blockIndex]
                const allRanges = getAllSelectedDateRanges
                return (
                  <DateRangeBlock
                    key={blockIndex}
                    blockIndex={blockIndex}
                    onDelete={handleRemoveTimeBlock}
                    isSeasonalActive={isSeasonalExperience}
                    initialStartDate={dateRange?.start ? dayjs(dateRange.start) : undefined}
                    initialEndDate={dateRange?.end ? dayjs(dateRange.end) : undefined}
                    allRanges={allRanges}
                    timeBlocks={timeBlocks}
                  />
                )
              })}
              <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeBlock} className="mt-m pl-0">
                {t("listings.tabs.scheduling.sections.seasons.add_time_block")}
              </Button>
              <Form.Item
                name="recurringYearly"
                valuePropName="checked"
                className="mt-l"
                initialValue={trip?.available_date_ranges?.length > 0 ? trip.recurring_yearly : true}
              >
                <Checkbox>{t("listings.tabs.scheduling.sections.seasons.recurring_yearly")}</Checkbox>
              </Form.Item>
            </>
          )}
        </Card>
        <DaysAndHours form={form} />
        <LengthAndRates form={form} />
      </CustomForm>
      <ListingActionButtons form={form} onFinish={onFinish} />
    </Spin>
  )
}
